/**
 * Evidence Center — shared model for Ops + Control Plane.
 * Product name: Verification Center (canonical operational audit trail).
 *
 * An incident has one verification timeline. Container logs are one source among
 * many (Runtime Doctor, API failures, deployment events, runtime health,
 * AI analysis, recommended actions). Full ELK is out of scope — this module
 * structures and displays evidence we already have, with honest stubs for
 * future log attachment.
 */

import { LABEL } from "./terminology";
import type { EvidenceRef, IncidentStateSlice, OperationalState } from "./operationalState";

export type EvidenceSourceKind =
  | "runtime_doctor"
  | "api_failure"
  | "container_logs"
  | "deployment_event"
  | "runtime_health"
  | "ai_analysis"
  | "recommended_action"
  | "dns"
  | "ssl"
  | "knowledge"
  | "backup"
  | "attention"
  | "verification"
  | "other";

export type EvidenceSeverity = "critical" | "high" | "medium" | "low" | "info";

export type EvidenceTimelineEntry = {
  id: string;
  source: EvidenceSourceKind;
  title: string;
  detail: string;
  observedAt: string | null;
  severity: EvidenceSeverity | null;
  /** Deep-link within the product when available — never invent URLs. */
  href: string | null;
  /** Attached log excerpt when fetched; null until loaded. */
  logLines: string[] | null;
  /** True when this slot is reserved for a future source. */
  stub: boolean;
};

export type EvidenceLogsState = {
  status: "not_loaded" | "loading" | "available" | "unavailable" | "stub";
  /** Host CLI fallback — still useful when API cannot reach Docker. */
  command: string | null;
  lines: string[];
  fetchedAt: string | null;
  error: string | null;
  message: string | null;
};

export type EvidenceCenterState = {
  /** Incident id when scoped; null for tenant-level evidence without an open incident. */
  incidentId: string | null;
  slug: string;
  title: string;
  summary: string;
  timeline: EvidenceTimelineEntry[];
  logs: EvidenceLogsState;
  recommendedActions: string[];
  /** AI analysis text when provided — never invented. */
  aiAnalysis: string | null;
  rootCause: string | null;
};

export type IncidentEvidenceInput = {
  kind: string;
  category?: string;
  label: string;
  value: string;
  observedAt?: string | null;
  observed_at?: string | null;
};

export type AssembleEvidenceCenterInput = {
  slug: string;
  /** Optional open incident id. */
  incidentId?: string | null;
  title?: string | null;
  summary?: string | null;
  /** Shared operational state when available. */
  operational?: Pick<
    OperationalState,
    | "evidence"
    | "incident"
    | "incidents"
    | "runtimeDoctor"
    | "runtimeHealth"
    | "rootCause"
    | "recommendedAction"
    | "dns"
    | "ssl"
    | "knowledge"
    | "deployment"
    | "logAttachment"
    | "verificationTimeline"
  > | null;
  /** Incident workspace evidence rows (backend categories). */
  incidentEvidence?: IncidentEvidenceInput[] | null;
  aiAnalysis?: string | null;
  recommendedAction?: string | null;
  rootCause?: string | null;
  logsCommand?: string | null;
  /** Pre-fetched container log lines (from GET .../logs). */
  logLines?: string[] | null;
  logsFetchedAt?: string | null;
  logsError?: string | null;
  logsAvailable?: boolean | null;
  logsMessage?: string | null;
};

const SOURCE_LABEL: Record<EvidenceSourceKind, string> = {
  runtime_doctor: "Runtime Doctor",
  api_failure: "API failure",
  container_logs: "Container logs",
  deployment_event: "Deployment",
  runtime_health: "Runtime Health",
  ai_analysis: "AI analysis",
  recommended_action: "Recommended action",
  dns: "DNS",
  ssl: "SSL",
  knowledge: "Knowledge Base",
  backup: "Backup",
  attention: "Attention",
  verification: "Verification",
  other: "Other",
};

export function evidenceSourceLabel(source: EvidenceSourceKind): string {
  return SOURCE_LABEL[source];
}

function mapCategoryToSource(category: string, kind: string): EvidenceSourceKind {
  const c = (category || kind || "").toLowerCase();
  if (c === "doctor_check" || c === "runtime_doctor") return "runtime_doctor";
  if (c === "health_endpoint" || c === "api_failure" || c === "health") return "api_failure";
  if (c === "logs" || c === "container_logs" || c === "log_placeholder") return "container_logs";
  if (c === "deployment" || c === "manifest" || c === "container_state") return "deployment_event";
  if (c === "backup") return "backup";
  if (c === "dns") return "dns";
  if (c === "ssl") return "ssl";
  if (c === "knowledge") return "knowledge";
  if (c === "attention") return "attention";
  if (c === "ai_analysis") return "ai_analysis";
  if (c === "recommended_action") return "recommended_action";
  if (c === "runtime_health") return "runtime_health";
  return "other";
}

function mapEvidenceRef(ref: EvidenceRef, index: number): EvidenceTimelineEntry {
  const source = mapCategoryToSource(ref.kind, ref.kind);
  return {
    id: `op-${source}-${index}-${ref.label}`,
    source,
    title: ref.label,
    detail: ref.value,
    observedAt: ref.observedAt,
    severity: source === "runtime_doctor" || source === "api_failure" ? "high" : "info",
    href: ref.url,
    logLines: null,
    stub: source === "container_logs" && ref.kind === "log_placeholder",
  };
}

function mapIncidentEvidence(
  item: IncidentEvidenceInput,
  index: number,
): EvidenceTimelineEntry {
  const category = item.category ?? item.kind;
  const source = mapCategoryToSource(category, item.kind);
  const observed = item.observedAt ?? item.observed_at ?? null;
  return {
    id: `inc-${source}-${index}-${item.label}`,
    source,
    title: item.label,
    detail: item.value,
    observedAt: observed ? String(observed) : null,
    severity:
      source === "runtime_doctor" || source === "api_failure"
        ? "high"
        : source === "container_logs"
          ? "info"
          : "medium",
    href: null,
    logLines: null,
    stub: false,
  };
}

function logsCommandFor(slug: string): string {
  if (!slug || slug === "host") {
    return "TENANT=nordic-demo ./deploy.sh demo logs";
  }
  return `TENANT=${slug} ./deploy.sh ${slug} logs`;
}

function pushUnique(
  timeline: EvidenceTimelineEntry[],
  entry: EvidenceTimelineEntry,
): void {
  if (timeline.some((e) => e.id === entry.id || (e.source === entry.source && e.title === entry.title && e.detail === entry.detail))) {
    return;
  }
  timeline.push(entry);
}

/**
 * Assemble one Verification Center timeline from operational state + incident rows.
 * Does not invent log content — attaches lines only when supplied.
 */
export function assembleEvidenceCenter(
  input: AssembleEvidenceCenterInput,
): EvidenceCenterState {
  const slug = input.slug.trim();
  const operational = input.operational ?? null;
  const incident: IncidentStateSlice | null =
    operational?.incident ??
    (input.incidentId
      ? (operational?.incidents.find((i) => i.id === input.incidentId) ?? null)
      : null);

  const timeline: EvidenceTimelineEntry[] = [];

  // 1) Incident evidence (backend) — preferred when workspace is open.
  for (const [index, item] of (input.incidentEvidence ?? []).entries()) {
    pushUnique(timeline, mapIncidentEvidence(item, index));
  }

  // 2) Shared operational evidence refs.
  for (const [index, ref] of (operational?.evidence ?? []).entries()) {
    pushUnique(timeline, mapEvidenceRef(ref, index));
  }

  // 3b) Structured verification audit trail (DNS, TLS, HTTP, application probes).
  for (const [index, entry] of (operational?.verificationTimeline ?? []).entries()) {
    pushUnique(timeline, {
      id: entry.id || `verification-${slug}-${index}`,
      source: "verification",
      title: entry.title,
      detail: [entry.detail, entry.recommendation].filter(Boolean).join(" · "),
      observedAt: entry.observedAt,
      severity:
        entry.status === "failed"
          ? "critical"
          : entry.status === "warning"
            ? "high"
            : "info",
      href: null,
      logLines: null,
      stub: false,
    });
  }

  // 4) Runtime Doctor summary.
  if (operational?.runtimeDoctor) {
    const doctor = operational.runtimeDoctor;
    pushUnique(timeline, {
      id: `doctor-${slug}`,
      source: "runtime_doctor",
      title: "Runtime Doctor",
      detail: [
        `Current result: ${doctor.currentResult}`,
        doctor.rootCause ? `Root cause: ${doctor.rootCause}` : null,
        doctor.recommendedAction ? `Action: ${doctor.recommendedAction}` : null,
      ]
        .filter(Boolean)
        .join(" · "),
      observedAt: doctor.lastScanAt,
      severity:
        doctor.currentResult === "Fail"
          ? "critical"
          : doctor.currentResult === "Warn"
            ? "high"
            : "info",
      href: doctor.reportHref || null,
      logLines: null,
      stub: false,
    });
  }

  // 5) Runtime Health components that are not Healthy / Not monitored.
  for (const row of operational?.runtimeHealth ?? []) {
    if (row.label === LABEL.notMonitored || row.label === "Healthy") continue;
    pushUnique(timeline, {
      id: `rh-${row.id}`,
      source: "runtime_health",
      title: row.name,
      detail: [row.label, row.reason, row.explanation].filter(Boolean).join(" — "),
      observedAt: null,
      severity: row.label === "Critical" ? "critical" : "medium",
      href: null,
      logLines: null,
      stub: false,
    });
  }

  // 5) Deployment metadata snapshot (informational).
  if (operational?.deployment) {
    const d = operational.deployment;
    pushUnique(timeline, {
      id: `deploy-${slug}`,
      source: "deployment_event",
      title: "Deployment metadata",
      detail: [
        d.platformVersion !== LABEL.notAvailable
          ? `version ${d.platformVersion}`
          : null,
        d.imageTag !== LABEL.notAvailable ? `image ${d.imageTag}` : null,
        d.deploymentTime !== LABEL.notAvailable
          ? `deployed ${d.deploymentTime}`
          : null,
      ]
        .filter(Boolean)
        .join(" · ") || LABEL.notAvailable,
      observedAt: null,
      severity: "info",
      href: null,
      logLines: null,
      stub: false,
    });
  }

  // 6) Recommended action + AI analysis.
  const recommended =
    input.recommendedAction ??
    operational?.recommendedAction ??
    incident?.recommendedAction ??
    null;
  if (recommended) {
    pushUnique(timeline, {
      id: `action-${slug}`,
      source: "recommended_action",
      title: "Recommended action",
      detail: recommended,
      observedAt: null,
      severity: "medium",
      href: null,
      logLines: null,
      stub: false,
    });
  }

  const aiAnalysis = (input.aiAnalysis ?? "").trim() || null;
  if (aiAnalysis) {
    pushUnique(timeline, {
      id: `ai-${slug}`,
      source: "ai_analysis",
      title: "AI analysis",
      detail: aiAnalysis,
      observedAt: null,
      severity: "info",
      href: null,
      logLines: null,
      stub: false,
    });
  }

  // 7) Container logs — attach real lines when fetched; otherwise honest stub.
  const command = input.logsCommand ?? logsCommandFor(slug);
  const hasLines = Array.isArray(input.logLines) && input.logLines.length > 0;
  const logsUnavailable = input.logsAvailable === false || Boolean(input.logsError);
  let logsStatus: EvidenceLogsState["status"] = "not_loaded";
  if (hasLines) logsStatus = "available";
  else if (logsUnavailable) logsStatus = "unavailable";
  else if (input.logsAvailable === true && !hasLines) logsStatus = "available";
  else logsStatus = "stub";

  const logDetail = hasLines
    ? `${input.logLines!.length} log line(s) attached`
    : logsUnavailable
      ? input.logsError || input.logsMessage || "Container logs unavailable"
      : "Container logs not loaded — open View Logs to fetch from the tenant stack";

  pushUnique(timeline, {
    id: `logs-${slug}`,
    source: "container_logs",
    title: "Container logs",
    detail: logDetail,
    observedAt: input.logsFetchedAt ?? null,
    severity: "info",
    href: null,
    logLines: hasLines ? input.logLines! : null,
    stub: !hasLines,
  });

  // Sort: stubs last; then by observedAt desc when present.
  timeline.sort((a, b) => {
    if (a.stub !== b.stub) return a.stub ? 1 : -1;
    const at = a.observedAt ? Date.parse(a.observedAt) : 0;
    const bt = b.observedAt ? Date.parse(b.observedAt) : 0;
    return bt - at;
  });

  const title =
    (input.title ?? "").trim() ||
    incident?.problem ||
    `Verification — ${slug}`;

  const summary =
    (input.summary ?? "").trim() ||
    (incident
      ? `${incident.severity}: ${incident.problem}`
      : "Verification timeline assembled from Runtime Doctor, DNS/TLS, integrations, infrastructure, and logs.");

  return {
    incidentId: input.incidentId ?? incident?.id ?? null,
    slug,
    title,
    summary,
    timeline,
    logs: {
      status: logsStatus,
      command,
      lines: hasLines ? [...input.logLines!] : [],
      fetchedAt: input.logsFetchedAt ?? null,
      error: input.logsError ?? null,
      message:
        input.logsMessage ??
        (logsStatus === "stub"
          ? "Fetch container logs via View Logs. CLI fallback remains available on the host."
          : null),
    },
    recommendedActions: recommended ? [recommended] : [],
    aiAnalysis,
    rootCause: input.rootCause ?? operational?.rootCause ?? incident?.problem ?? null,
  };
}

/** Canonical product name — Verification Center. */
export const assembleVerificationCenter = assembleEvidenceCenter;
export type VerificationCenterState = EvidenceCenterState;
export type AssembleVerificationCenterInput = AssembleEvidenceCenterInput;
